import { computed, effect, Injectable, signal } from '@angular/core';
import { RolesIdsEnum } from 'app/_models/roles';

import { User, UserTokenPayload } from 'app/_models/user';
import { UserAllowedPage } from 'app/_models/userAllowedPage';
import { UserCashbackData } from 'app/_models/userCashbackData';
import { UserMultiloginData } from 'app/_models/userMultiloginData';
import { b64DecodeUnicode } from 'app/_utils/general-utils';
import { LocalStorageUtils } from 'app/_utils/localstorage';
import { UserLoginResponse } from 'app/account/login/_models/userLoginResponse';

@Injectable({ providedIn: 'root' })
export class UserService {
    private localStorage: LocalStorageUtils = new LocalStorageUtils();

    public buyerRoles = [RolesIdsEnum.Comprador, RolesIdsEnum.DonoDaOficina, RolesIdsEnum.SupervisorOficina];
    public centralRoles = [RolesIdsEnum.Central, RolesIdsEnum.CentralSuporte];
    public sellerRoles = [RolesIdsEnum.Vendedor, RolesIdsEnum.VendedorCommerce];
    public supportRoles = [RolesIdsEnum.Comercial, RolesIdsEnum.Faturamento, RolesIdsEnum.GestorFaturamento, RolesIdsEnum.Suporte, RolesIdsEnum.SuporteSuperAdmin];

    private userState = signal<User | null>(null);

    public user = this.userState.asReadonly();

    public companyId = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.company_id ?? user.company_id;
    });

    public holdingId = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.holding_id ?? user.holding_id;
    });

    public userId = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.id ?? user.id;
    });

    public userCompanyId = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.user_company_id ?? user.user_company_id;
    });

    public userHasCashback = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.has_cashback ?? user.has_cashback;
    });

    public userPlan = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.plan ?? user.plan;
    });

    userPurchaseThroughApi = computed(() => {
        const user = this.user();
        if (!user) return false;

        return user?.selectedUser?.purchase_through_api ?? user.purchase_through_api;
    });

    public userRole = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.selectedUser?.role ?? user.role;
    });

    public userIsBuyer = computed(() => {
        const user = this.user();
        if (!user) return false;

        return this.buyerRoles.some(r => r == user.role);
    });

    public userIsCentral = computed(() => {
        const user = this.user();
        if (!user) return false;

        return user.isAdmin && this.centralRoles.some(r => r == user.role);
    });

    public userIsSeller = computed(() => {
        const user = this.user();
        if (!user) return false;

        return this.sellerRoles.some(r => r == user.role);
    });

    public userIsSupport = computed(() => {
        const user = this.user();
        if (!user) return null;

        return user.isAdmin && this.supportRoles.some(r => r == user.role);
    });

    constructor() {
        effect(() => {
            if (!this.user()) return;
            this.localStorage.salvarDadosUsuario(this.user());
        });
    }

    public logoutUser() {
        this.userState.set(null);
        this.localStorage.limparDadosUsuario();
    }

    public setSelectedUser(selectedUser: UserMultiloginData) {
        this.userState.update(user => {
            if (!user) return user;

            return { ...user, selectedUser };
        });
    }

    public setCashbackData(cashbackData?: UserCashbackData) {
        this.userState.update(user => {
            if (!user) return user;

            return { ...user, cashback: cashbackData }
        });
    }

    public setUserAcceptTermsOfUse() {
        this.userState.update(user => {
            if (!user) return user;

            return { ...user, is_use_terms_accepted: true };
        })
    }

    public setUserAllowedPages(selectedPages: UserAllowedPage[]) {
        this.userState.update(user => {
            if (!user) return user;

            const homePage = selectedPages[0].subPages.length  > 0 ? `${selectedPages[0].url}/${selectedPages[0].subPages[0]}` : selectedPages[0].url;

            return { ...user, pages: selectedPages, homePage: homePage };
        })
    }

    public setUserFirstLoginDate(firstLoginDate: Date) {
        this.userState.update(user => {
            if (!user) return user;

            return { ...user, first_login_date: firstLoginDate }
        })
    }

    public setUserMultiloginData(multiloginData: UserMultiloginData[]) {
        this.userState.update(user => {
            if (!user) return user;

            const selectedUser = this.userIsCentral() ? multiloginData[0] : multiloginData.find(u => u.id == user.id);
            return { ...user, dadosMultiUsuario: multiloginData, selectedUser  }
        })
    }

    public setUserStateFromCookie(userCookie: User) {
        this.userState.set(userCookie);
    }

    public setUserStateFromLoginResponse(userLogin: UserLoginResponse) {
        this.userState.update((user) => {
            const tokenPayload = this.obterTokenPayload(userLogin.token);

            const userFromLogin: User = {
                id: userLogin.id,
                name: tokenPayload.name,
                firstName: tokenPayload.name,
                username: userLogin.username,
                token: userLogin.token,
                chat_id: userLogin.chatId,
                culture: tokenPayload.culture,
                version: tokenPayload.version,

                user_company_id: tokenPayload.user_company_id,
                holding_id: userLogin.holdingId,
                email: tokenPayload.email,
                role: tokenPayload.role,
                plan: tokenPayload.plan,
                company_id: tokenPayload.company_id,

                current_company_name: userLogin.currentCompanyName,
                isAdmin: tokenPayload.is_admin,
                is_use_terms_accepted: userLogin.isUseTermsAccepted,

                has_cashback: userLogin.hasCashback,
                part_type: userLogin.partType,
                holding_type_id: userLogin.holdingTypeId,
                is_billable: userLogin.isBillable,
                first_login_date: userLogin.firstLoginDate,
                user_ip: userLogin.userIP,

                homePage: null,
                selectedUser: null,
            };

            return { ...user, ...userFromLogin}
        } );
    }

    private obterTokenPayload(token: string): UserTokenPayload {
        if (!token)
            return null;

        const tokenData = JSON.parse(atob(token.split('.')[1]));

        const tokenPayload = {
            company_id: parseInt(tokenData.companyId),
            email: b64DecodeUnicode(tokenData.email),
            is_admin: tokenData.isAdmin == 'True',
            name: b64DecodeUnicode(tokenData.givenName),
            plan: parseInt(tokenData.plan),
            role: parseInt(tokenData.role),
            user_company_id: parseInt(tokenData.userCompanyId),
            culture: b64DecodeUnicode(tokenData.culture),
            version: b64DecodeUnicode(tokenData.version)
        };

        return tokenPayload;
    }
}
